import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { doc, getDoc } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { motion } from 'framer-motion'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { ALERTS } from '../styles/classNames'
import { useAuth } from '@/core/AuthContext'
import { usePhoneAuth } from '../lib/usePhoneAuth'
import { auth, db, functions } from '@/core/firebase'

const INPUT_CLASS = 'w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent'
const CODE_INPUT_CLASS = `${INPUT_CLASS} text-center text-2xl tracking-widest`

export function TrainerPhoneLoginPage() {
  const navigate = useNavigate()
  const { setSkipAutoCreateUser, refreshUserData } = useAuth()

  const [needsAccountCreation, setNeedsAccountCreation] = useState(false)
  const [name, setName] = useState('')
  const [accountLoading, setAccountLoading] = useState(false)
  const [accountError, setAccountError] = useState<string | null>(null)

  const {
    phoneNumber,
    setPhoneNumber,
    verificationCode,
    setVerificationCode,
    confirmation,
    loading,
    error,
    sendCode,
    verifyCode,
    reset: baseReset,
    userData,
    authLoading,
  } = usePhoneAuth({
    skipAutoCreate: true,
    onVerified: async () => {
      const currentUser = auth.currentUser
      if (!currentUser) {
        throw new Error('Verification succeeded, but no authenticated user was found.')
      }

      const userDoc = await getDoc(doc(db, 'users', currentUser.uid))
      const role = userDoc.exists() ? userDoc.data().role : null

      if (role === 'trainer') {
        // Existing trainer - refresh userData and go to dashboard
        setSkipAutoCreateUser(false)
        await refreshUserData()
        navigate('/trainer', { replace: true })
        return
      }

      // New user or client trying trainer flow - show account creation
      setNeedsAccountCreation(true)
    },
  })

  // Check if already logged in as trainer on mount
  if (!authLoading && userData?.role === 'trainer' && !confirmation && !needsAccountCreation) {
    navigate('/trainer', { replace: true })
    return null
  }

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault()
    setAccountLoading(true)
    setAccountError(null)

    try {
      // Create trainer account via Cloud Function
      const createTrainerAccount = httpsCallable(functions, 'createTrainerAccount')
      await createTrainerAccount({ name: name.trim() })

      // Refresh ID token to get new custom claims
      await auth.currentUser?.getIdToken(true)

      // Refresh userData from Firestore to get role='trainer'
      const refreshedUserData = await refreshUserData()

      if (refreshedUserData?.role !== 'trainer') {
        throw new Error('Trainer account was created, but trainer permissions are not ready yet. Please try again.')
      }

      setSkipAutoCreateUser(false)

      // Let AuthContext publish the refreshed trainer role before ProtectedRoute
      // evaluates the trainer dashboard route.
      window.setTimeout(() => {
        navigate('/trainer', { replace: true })
      }, 0)
    } catch (err: any) {
      console.error('[TrainerPhoneLoginPage] Account creation error:', err)
      setAccountError(err?.message || 'Failed to create trainer account. Please try again.')
      setSkipAutoCreateUser(false)
    } finally {
      setAccountLoading(false)
    }
  }

  const handleReset = () => {
    baseReset()
    setNeedsAccountCreation(false)
    setName('')
    setAccountError(null)
  }

  const displayError = accountError || error

  return (
    <div className="min-h-screen flex items-center justify-center p-4 safe-all" data-testid="trainer-auth-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full"
      >
        <Card variant="elevated" padding="lg" className="shadow-2xl">
          <div className="text-center mb-8">
            <motion.h1
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="text-4xl font-bold mb-2 bg-gradient-to-r from-primary-500 via-primary-600 to-accent-500 bg-clip-text text-transparent"
            >
              Trainer Sign In
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-gray-600 dark:text-slate-400 text-lg"
            >
              {needsAccountCreation
                ? 'Create your trainer account'
                : confirmation
                  ? 'Enter verification code'
                  : 'Sign in or sign up with your phone'}
            </motion.p>
          </div>

          {displayError && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className={`mb-6 text-sm ${ALERTS.error}`}
            >
              {displayError}
            </motion.div>
          )}

          {!confirmation ? (
            <motion.form
              onSubmit={sendCode}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="space-y-4"
            >
              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2"
                >
                  Phone Number
                </label>
                <input
                  id="phone"
                  data-testid="trainer-auth-phone-input"
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="(555) 123-4567"
                  required
                  className={INPUT_CLASS}
                />
              </div>

              <div
                id="recaptcha-container"
                className="recaptcha-container--hidden"
              />

              <Button
                type="submit"
                data-testid="trainer-auth-send-code-button"
                disabled={loading || !phoneNumber}
                isLoading={loading}
                variant="primary"
                size="lg"
                className="w-full"
              >
                {loading ? 'Sending code...' : 'Send verification code'}
              </Button>
            </motion.form>
          ) : !needsAccountCreation ? (
            <motion.form
              onSubmit={verifyCode}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="space-y-4"
            >
              <div>
                <label
                  htmlFor="code"
                  className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2"
                >
                  Verification Code
                </label>
                <input
                  id="code"
                  data-testid="trainer-auth-code-input"
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="one-time-code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  required
                  maxLength={6}
                  className={CODE_INPUT_CLASS}
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">
                  Check your phone for a 6-digit code
                </p>
              </div>

              <Button
                type="submit"
                data-testid="trainer-auth-verify-code-button"
                disabled={loading || verificationCode.length !== 6}
                isLoading={loading}
                variant="primary"
                size="lg"
                className="w-full"
              >
                {loading ? 'Verifying...' : 'Verify code'}
              </Button>

              <Button
                type="button"
                data-testid="trainer-auth-reset-button"
                onClick={handleReset}
                disabled={loading}
                variant="ghost"
                size="md"
                className="w-full"
              >
                Use different number
              </Button>
            </motion.form>
          ) : (
            <motion.form
              onSubmit={handleCreateAccount}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="space-y-4"
            >
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2"
                >
                  Your Name
                </label>
                <input
                  id="name"
                  data-testid="trainer-auth-name-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  required
                  className={INPUT_CLASS}
                />
              </div>

              <Button
                type="submit"
                data-testid="trainer-auth-create-account-button"
                disabled={accountLoading || !name.trim()}
                isLoading={accountLoading}
                variant="primary"
                size="lg"
                className="w-full"
              >
                {accountLoading ? 'Creating account...' : 'Create trainer account'}
              </Button>

              <Button
                type="button"
                onClick={handleReset}
                disabled={accountLoading}
                variant="ghost"
                size="md"
                className="w-full"
                data-testid="start-over-button"
              >
                Start over
              </Button>
            </motion.form>
          )}

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mt-6 text-center text-sm text-gray-600 dark:text-slate-400"
          >
            <p>
              By continuing, you agree to receive phone calls for workout reminders.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-4 pt-4 border-t border-gray-200 dark:border-white/10 text-center"
          >
            <p className="text-sm text-gray-600 dark:text-slate-400">
              Are you a client?{' '}
              <Link
                to="/phone-login"
                className="text-primary-600 dark:text-primary-400 hover:underline font-medium"
                data-testid="client-phone-login-link"
              >
                Sign in here
              </Link>
            </p>
          </motion.div>
        </Card>
      </motion.div>
    </div>
  )
}
